const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { isFunction } = require('@/functions/is');

/**
 * @constructor
 *
 * @param $rootScope
 * @param {ApiClient} apiClient
 * @param {AppConfig} appConfig
 * @param {EventBus} eventBus
 * @param {UserService} userService
 */
const ConnectivityChecker = function (
    $rootScope,
    apiClient,
    appConfig,
    eventBus,
    userService
) {
    this.$rootScope = $rootScope;
    this.apiClient = apiClient;
    this.appConfig = appConfig;
    this.eventBus = eventBus;
    this.userService = userService;

    // Listen for any failing network requests and enter offline mode if one is observed.
    this.$rootScope.$on('requestError', function (event, response) {
        if (this.online && (response.status === 0 || response.status === 503)) {
            // Network error
            this.online = false;
            this.notifyObservers();
        }
    }.bind(this));

    // Listen for any successful network requests and enter online mode if one is observed.
    // FIXME: This fires on *any* successful network activity. Not necessarily a request to the DT API.
    // this.$rootScope.$on('requestSuccess', function () {
    //     if (!this.online) {
    //         // Network success
    //         this.online = true;
    //         this.notifyObservers();
    //     }
    // }.bind(this));

    /**
     * @type {ConsoleLogger}
     */
    this.log = logFactory('ConnectivityChecker', false);

    /**
     * Interval for checking if still connected.
     *
     * @type {?number}
     */
    this.timer = null;

    /**
     * How frequently (in seconds) to check if still connected.
     *
     * @type {number}
     */
    this.seconds = this.appConfig.statusCheckFrequency;

    /**
     * Connected?
     *
     * @type {?boolean}
     */
    this.online = null;

    /**
     * @type {Object<{key: string, notifyOnline: function, notifyOffline: function}>}
     */
    this.observerList = {};

    /**
     * Registered listener functions that will receive the response from the DT status endpoint every time it is called,
     * not only when there is a switch to/from offline mode.
     *
     * @type {Object.<string, function>}
     */
    this.statusObservers = {};

    this.start();
};

ConnectivityChecker.prototype = {
    start() {
        if (this.timer !== null) {
            throw new Error('Cannot start interval because it is already running.');
        }

        this.log.debug('Started (' + this.seconds + 's)');
        this.timer = setInterval(this.check.bind(this), this.seconds * 1000);

        // Call it once now
        setTimeout(
            () => {
                this.check().catch(() => {});
            },
            200
        );
    },

    stop() {
        if (this.timer != null) {
            this.log.debug('Stopped');
            clearInterval(this.timer);
        }
        this.timer = null;
    },

    /**
     * Make an actual API request to check if we are online.
     * Resolves with a promise with a true/false value.
     *
     * @returns {Promise<boolean>}
     */
    check() {
        return new Promise((resolve) => {
            this.log.debug('Pinging server...');

            this.apiClient.request('GET', 'status')
                .then((result) => {
                    if (!result || !result.data || !result.data.time) {
                        // We loaded something, but it wasn't what was expected. Go offline.
                        throw new Error('Invalid status response.');
                    }

                    if (this.online === null || this.online === false) {
                        // Previously was offline, now we are online..
                        this.online = true;
                        this.log.debug('Change - Notifying');
                        this.notifyObservers();
                    }

                    Object.values(this.statusObservers).forEach((statusObserver) => {
                        statusObserver(result);
                    });

                    resolve(true);
                    this.log.debug('Complete - Online');
                })
                .catch((error) => {
                    if (this.userService && this.userService.isLoggedIn()) {
                        // If the message from the API is a branch error, refresh the user.
                        // If we need to introduce more tests then move the test logic see: findNonRecoverableErrors.js
                        if (/Branch|not available/.test(error.message)) {
                            this.userService.setAutofillLoginUsername(this.userService.getUsername());
                            this.userService.setLoginErrorMessageKey('LOGIN.ERROR__BRANCH_UNAVAILABLE');

                            // Display "Please wait..." while logging out instead of "Logging out..." because the user
                            // doesn't really want to be logging out!
                            this.userService.logout('LOGIN.LOGGING_OUT__FORCED');
                            return;
                        }
                    }

                    if (this.online === null || this.online === true) {
                        // Previously was online, now we are offline..
                        this.online = false;
                        this.log.debug('Change - Notifying');
                        this.notifyObservers();
                    }

                    resolve(false);
                    this.log.debug('Complete - Offline');
                });
        });
    },

    notifyObservers() {
        this.eventBus.broadcast(EventNames.ONLINE_STATUS_CHANGED);

        // eslint-disable-next-line unused-imports/no-unused-vars
        for (let key in this.observerList) {
            /* istanbul ignore if */
            if (!this.observerList.hasOwnProperty(key)) {
                continue;
            }

            let observer = this.observerList[key];

            if (this.online === true) {
                observer.notifyOnline(this);
            } else if (this.online === false) {
                observer.notifyOffline(this);
            }
        }
    },

    hasObserver(key) {
        return this.observerList.hasOwnProperty(key);
    },

    addObserver(observer, notifyNow) {
        // Check if they have the required methods
        if (
            !isFunction(observer.notifyOnline)
            || !isFunction(observer.notifyOffline)
            || !observer.hasOwnProperty('key')
        ) {
            throw new Error(
                'Observer must have a key and implement notifyOnline() and notifyOffline() methods.'
            );
        }

        if (this.hasObserver(observer.key)) {
            this.log.debug(`An observer already exists with the key '${observer.key}', replacing.`);
        }

        this.log.debug('Added observer with the key ' + observer.key);
        this.observerList[observer.key] = observer;

        // Notify that observer now.
        if (notifyNow) {
            if (this.online) {
                observer.notifyOnline(this);
            } else {
                observer.notifyOffline(this);
            }
        }
    },

    removeObserver(key) {
        if (this.observerList.hasOwnProperty(key)) {
            delete this.observerList[key];
            return true;
        }

        return false;
    },

    /**
     * Register a function that will be called with the result of the /status API call each time it is received.
     *
     * @param {string} key
     * @param {function} func
     */
    addStatusObserver(key, func) {
        this.statusObservers[key] = func;
    },

    /**
     * Returns the cached 'is online' answer.
     * You can use 'check()' instead of make an actual request to check and update the online state in the process.
     *
     * @returns {?boolean}
     */
    isOnline() {
        return this.online;
    },
};

module.exports = ConnectivityChecker;
